import { useState, useRef, useCallback, useEffect } from 'react'
import { WEBSOCKET_ENDPOINTS } from '../contracts/endpoints'
import type { ConversationMessage, ClaudeWebSocketMessage, ClaudeWebSocketResponse, FnaFormData, FileAttachment, ResultsSummary } from '../types'
import { API_BASE_URL } from '../services/api'
import { resolveWebSocketUrl } from '../services/urls'
import {
  beginIntentionalClose,
  clearSocketReference,
  getPreOpenRejectionMessage,
  getWebSocketCloseError,
  markWebSocketOpened,
  prepareWebSocketConnect,
  registerSlotReleaseOnPageHide,
  scheduleReconnect,
  startKeepAlive,
  stopKeepAlive,
} from './websocketLifecycle'

const CLAUDE_WS_OVERRIDE = import.meta.env.VITE_CLAUDE_WS_URL

interface ClaudeWebSocketState {
  isConnected: boolean
  isLoading: boolean
  error: string | null
  conversationId: number | null
}

/**
 * Callback for when conversation history is loaded from the server.
 */
type ConversationLoadedCallback = (
  conversationId: number,
  history: Array<{ role: 'user' | 'assistant'; content: string }>,
  hasSummary: boolean
) => void

/**
 * Custom hook for managing WebSocket connection to Claude AI agent.
 *
 * @param resultsSummary - Optional results summary context for Claude
 * @returns Object with WebSocket state and control functions
 */
export const useClaudeWebSocket = (resultsSummary?: ResultsSummary | null) => {
  const [state, setState] = useState<ClaudeWebSocketState>({
    isConnected: false,
    isLoading: false,
    error: null,
    conversationId: null
  })

  const socketRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<number | null>(null)
  const reconnectAttemptRef = useRef(0)
  const intentionalCloseRef = useRef(false)
  const isMountedRef = useRef(false)
  const keepAliveIntervalRef = useRef<number | null>(null)
  const onMessageCallbackRef = useRef<((data: ClaudeWebSocketResponse) => void) | null>(null)
  const onConversationLoadedRef = useRef<ConversationLoadedCallback | null>(null)
  const lastContextHashRef = useRef<string | null>(null)

  /**
   * Establishes WebSocket connection to Claude agent.
   */
  const connect = useCallback(() => {
    try {
      if (!prepareWebSocketConnect({
        socketRef,
        reconnectTimeoutRef,
        reconnectAttemptRef,
        intentionalCloseRef,
        isMountedRef,
      })) {
        return
      }

      const wsUrl = resolveWebSocketUrl(WEBSOCKET_ENDPOINTS.askClaude, {
        baseUrl: API_BASE_URL,
        overrideUrl: CLAUDE_WS_OVERRIDE,
      })

      const socket = new WebSocket(wsUrl)
      let socketOpened = false
      socketRef.current = socket
      setState(prev => ({
        ...prev,
        error: null
      }))

      socket.onopen = () => {
        if (socketRef.current !== socket) {
          return
        }

        socketOpened = true
        markWebSocketOpened(reconnectAttemptRef, reconnectTimeoutRef)
        stopKeepAlive(keepAliveIntervalRef)
        keepAliveIntervalRef.current = startKeepAlive(socketRef)
        setState(prev => ({
          ...prev,
          isConnected: true,
          error: null
        }))
        // Connection status is shown in the UI indicator, no need to display a chat message
      }

      socket.onmessage = (event) => {
        if (socketRef.current !== socket) {
          return
        }

        try {
          const data = JSON.parse(event.data)

          // Keepalive acknowledgement; not a chat payload.
          if (data.type === 'pong') {
            return
          }

          // Handle conversation management messages
          if (data.type === 'conversation_loaded' || data.type === 'conversation_switched') {
            setState(prev => ({
              ...prev,
              conversationId: data.conversation_id
            }))
            if (onConversationLoadedRef.current) {
              onConversationLoadedRef.current(
                data.conversation_id,
                data.history || [],
                data.has_summary || false
              )
            }
            return
          }

          if (data.type === 'conversation_created') {
            setState(prev => ({
              ...prev,
              conversationId: data.conversation_id
            }))
            return
          }

          if (data.type === 'context_ack') {
            return
          }

          // Handle regular messages
          if (onMessageCallbackRef.current) {
            onMessageCallbackRef.current(data as ClaudeWebSocketResponse)
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            stack: error instanceof Error ? error.stack : undefined,
            context: { rawData: event.data }
          })

          if (onMessageCallbackRef.current) {
            onMessageCallbackRef.current({
              error: 'Could not parse server response'
            })
          }
        }
      }

      socket.onclose = (event) => {
        stopKeepAlive(keepAliveIntervalRef)
        clearSocketReference(socketRef, socket)
        if (socketRef.current && socketRef.current !== socket) {
          return
        }
        lastContextHashRef.current = null

        if (!isMountedRef.current) {
          return
        }

        setState(prev => ({
          ...prev,
          isConnected: false
        }))

        const closeError = getWebSocketCloseError(event.code, event.reason)
        if (closeError) {
          setState(prev => ({
            ...prev,
            error: closeError
          }))
          return
        }

        if (!socketOpened) {
          setState(prev => ({
            ...prev,
            error: getPreOpenRejectionMessage('chat connection'),
          }))
          return
        }

        scheduleReconnect({
          socketRef,
          reconnectTimeoutRef,
          reconnectAttemptRef,
          intentionalCloseRef,
          isMountedRef,
        }, connect)
      }

      socket.onerror = (error) => {
        if (socketRef.current !== socket) {
          return
        }

        console.error('WebSocket error:', {
          message: 'WebSocket connection error',
          context: { error }
        })

        setState(prev => ({
          ...prev,
          error: 'Connection error. Please check your connection and try again.'
        }))
        // Connection status shown via UI indicator, no chat message needed
      }
    } catch (error) {
      console.error('Error establishing WebSocket connection:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        context: { error }
      })
    }
  }, [])

  const disconnectSocket = useCallback((resetState: boolean) => {
    beginIntentionalClose(intentionalCloseRef, reconnectTimeoutRef)
    stopKeepAlive(keepAliveIntervalRef)

    const socket = socketRef.current
    socketRef.current = null
    socket?.close()

    if (!resetState || !isMountedRef.current) {
      return
    }

    setState({
      isConnected: false,
      isLoading: false,
      error: null,
      conversationId: null
    })
  }, [])

  /**
   * Sends a message through the WebSocket connection.
   *
   * @param text - The message text to send
   * @param conversationHistory - Current conversation history
   * @param formData - Optional form data context
   * @param attachments - Optional file attachments (images and PDFs)
   * @returns Promise<void>
   */
  const sendMessage = useCallback(async (
    text: string,
    conversationHistory: ConversationMessage[],
    formData?: FnaFormData | null,
    attachments?: FileAttachment[]
  ): Promise<void> => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      setState(prev => ({
        ...prev,
        error: 'Not connected to server. Please wait for reconnection.'
      }))

      if (onMessageCallbackRef.current) {
        onMessageCallbackRef.current({
          error: 'Not connected to server. Please wait for reconnection.'
        })
      }
      return
    }

    try {
      setState(prev => ({ ...prev, isLoading: true }))

      // Get form data from localStorage if available
      let storedFormData = formData
      if (!storedFormData) {
        try {
          const storedData = localStorage.getItem('form_data')
          if (storedData) {
            storedFormData = JSON.parse(storedData)
          }
        } catch (error) {
          console.error('Error parsing form data from localStorage:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            stack: error instanceof Error ? error.stack : undefined,
            context: { error }
          })
        }
      }

      const messagePayload: ClaudeWebSocketMessage = {
        type: 'message',
        message: text,
        conversation_history: conversationHistory,
        form_data: storedFormData
      }

      // Include attachments if provided
      if (attachments && attachments.length > 0) {
        messagePayload.attachments = attachments
      }

      // Include results summary if available
      if (resultsSummary) {
        messagePayload.results_summary = resultsSummary
      }

      socketRef.current.send(JSON.stringify(messagePayload))
    } catch (error) {
      console.error('Error sending message:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        context: { error }
      })

      setState(prev => ({
        ...prev,
        error: 'Failed to send message'
      }))
    } finally {
      setState(prev => ({ ...prev, isLoading: false }))
    }
  }, [resultsSummary])
  useEffect(() => {
    if (!resultsSummary) {
      return
    }

    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      return
    }

    try {
      const storedData = localStorage.getItem('form_data')
      let formData: FnaFormData | null = null
      if (storedData) {
        formData = JSON.parse(storedData)
      }

      const contextPayload: ClaudeWebSocketMessage = {
        type: 'context_update',
        form_data: formData,
        results_summary: resultsSummary
      }

      const payloadHash = JSON.stringify(contextPayload)
      if (lastContextHashRef.current === payloadHash) return

      socketRef.current.send(JSON.stringify(contextPayload))
      lastContextHashRef.current = payloadHash
    } catch (error) {
      console.error('Error sending context update:', error)
    }
  }, [resultsSummary, state.isConnected])

  /**
   * Registers a callback function to handle incoming messages.
   *
   * @param callback - Function to call when messages are received
   */
  const onMessage = useCallback((callback: (data: ClaudeWebSocketResponse) => void) => {
    onMessageCallbackRef.current = callback
  }, [])

  /**
   * Registers a callback for when conversation is loaded or switched.
   *
   * @param callback - Function to call when conversation is loaded
   */
  const onConversationLoaded = useCallback((callback: ConversationLoadedCallback) => {
    onConversationLoadedRef.current = callback
  }, [])

  /**
   * Switches to a different conversation.
   *
   * @param conversationId - ID of the conversation to switch to
   */
  const switchConversation = useCallback((conversationId: number): void => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.error('Cannot switch conversation: WebSocket not connected')
      return
    }

    try {
      socketRef.current.send(JSON.stringify({
        type: 'switch_conversation',
        conversation_id: conversationId
      }))
    } catch (error) {
      console.error('Error switching conversation:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        context: { conversationId, error }
      })
    }
  }, [])

  /**
   * Creates a new conversation.
   */
  const createNewConversation = useCallback((): void => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.error('Cannot create conversation: WebSocket not connected')
      return
    }

    try {
      socketRef.current.send(JSON.stringify({
        type: 'new_conversation'
      }))
    } catch (error) {
      console.error('Error creating new conversation:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        context: { error }
      })
    }
  }, [])

  /**
   * Disconnects the WebSocket connection.
   */
  const disconnect = useCallback(() => {
    disconnectSocket(true)
  }, [disconnectSocket])

  // Auto-connect on mount, cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true
    connect()
    const unregisterPageHide = registerSlotReleaseOnPageHide(socketRef)

    return () => {
      isMountedRef.current = false
      unregisterPageHide()
      disconnectSocket(false)
    }
  }, [connect, disconnectSocket])

  return {
    isConnected: state.isConnected,
    isLoading: state.isLoading,
    error: state.error,
    conversationId: state.conversationId,
    sendMessage,
    onMessage,
    onConversationLoaded,
    switchConversation,
    createNewConversation,
    disconnect,
    reconnect: connect
  }
}
